import { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Plus, Pencil, Trash2, X, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

export default function ServicesAdminPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', summary: '', icon: 'cog', is_active: true, meta_title: '', meta_description: '' });

  const loadData = () => {
    setLoading(true);
    adminAPI.getServices().then(r => setServices(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const resetForm = () => {
    setForm({ name: '', summary: '', icon: 'cog', is_active: true, meta_title: '', meta_description: '' });
    setEditing(null);
    setShowForm(false);
  };

  const startEdit = (s) => {
    setForm({ name: s.name, summary: s.summary, icon: s.icon, is_active: s.is_active, meta_title: s.meta_title || '', meta_description: s.meta_description || '' });
    setEditing(s.id);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Hizmet adi zorunlu'); return; }
    try {
      if (editing) {
        await adminAPI.updateService(editing, form);
        toast.success('Hizmet guncellendi');
      } else {
        await adminAPI.createService(form);
        toast.success('Hizmet eklendi');
      }
      resetForm();
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata olustu'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu hizmeti silmek istediginize emin misiniz?')) return;
    try {
      await adminAPI.deleteService(id);
      toast.success('Hizmet silindi');
      loadData();
    } catch { toast.error('Silinemedi'); }
  };

  return (
    <div data-testid="admin-services">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Hizmetler</h1>
        <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-service-btn">
          <Plus className="h-4 w-4 mr-2" /> Hizmet Ekle
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{editing ? 'Hizmet Duzenle' : 'Yeni Hizmet'}</CardTitle>
            <button onClick={resetForm}><X className="h-5 w-5" /></button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Hizmet Adi *</Label><Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} data-testid="service-name-input" /></div>
              <div><Label>Ikon</Label><Input value={form.icon} onChange={e => setForm(f => ({...f, icon: e.target.value}))} placeholder="Lucide ikon adi" /></div>
            </div>
            <div><Label>Ozet</Label><textarea value={form.summary} onChange={e => setForm(f => ({...f, summary: e.target.value}))} rows={2} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>SEO Title</Label><Input value={form.meta_title} onChange={e => setForm(f => ({...f, meta_title: e.target.value}))} /></div>
              <div><Label>SEO Description</Label><Input value={form.meta_description} onChange={e => setForm(f => ({...f, meta_description: e.target.value}))} /></div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({...f, is_active: v}))} />
              <Label>Aktif</Label>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSubmit} data-testid="service-submit">{editing ? 'Guncelle' : 'Kaydet'}</Button>
              <Button variant="outline" onClick={resetForm}>Iptal</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="grid gap-3">
          {services.map(s => (
            <Card key={s.id} data-testid={`service-row-${s.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{s.name}</h3>
                    <Badge variant={s.is_active ? 'default' : 'secondary'} className="text-xs">{s.is_active ? 'Aktif' : 'Pasif'}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">{s.summary}</p>
                  <p className="text-xs text-muted-foreground font-mono mt-0.5">/{s.slug}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(s)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(s.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
